/**
 * Cross-module X XWP-16 (02_DESIGN §3.2; brief §3.5, §3.6, §16) — the pure model of the /magasins
 * workspace: node ids and the URL state, the tree « Stock global → Magasins → Emplacements » built
 * level by level from `GET /magasins/hierarchie`, the detail requests (levels 1 and 2, server sort
 * and search), the targeted refresh rule on topic `stock` (which loaded data a key touches), and
 * the models of the « Répartir », « Transférer », « Ajuster la répartition », magasin and
 * emplacement dialogs. Every rule the server enforces is checked again here only to say it before
 * sending (the server stays the authority); the sentences are the server's. Nothing touches the
 * DOM — rendering lives in pages/magasins and components/magasins.
 */
import {
  EMPLACEMENT_CHAMPS,
  HIERARCHIE,
  LISTE_TRANSFERTS,
  MAGASINS_PARAMS,
  MAGASINS_ROUTE,
  MAGASINS_VUES,
  MAGASIN_CHAMPS,
  NOEUD_RACINE,
  ONGLETS_MAGASIN,
  PREFIXES_NOEUD,
  REPARTITION_CHAMPS,
  STATUTS_MAGASIN,
  TRANSFERT_CHAMPS,
  TRI_EMPLACEMENTS,
  TRI_HIERARCHIE,
  TYPE_MAGASIN_COMPTOIR,
  TYPE_MAGASIN_DEFAUT,
  TYPE_MAGASIN_PRINCIPAL,
} from "@/config/magasins.config.js";
import { AJUSTER, FORM_EMPLACEMENT, FORM_MAGASIN, REPARTIR, TRANSFERER } from "@/config/magasins.messages.js";
import { CLES_STOCK_UI, SORT_PARAMS } from "@/config/ui.config.js";
import { can } from "@/config/permissions.config.js";

// ─── Node ids (`?noeud=`) ────────────────────────────────────────────────────────────────────────

export const noeudMagasin = (magasinId) => `${PREFIXES_NOEUD.MAGASIN}:${magasinId}`;
export const noeudEmplacement = (magasinId, emplacementId) => `${PREFIXES_NOEUD.EMPLACEMENT}:${magasinId}:${emplacementId}`;
export const noeudNonRange = (magasinId) => `${PREFIXES_NOEUD.NON_RANGE}:${magasinId}`;

const RACINE = Object.freeze({ id: NOEUD_RACINE, type: "racine", magasinId: null, emplacementId: null });

/**
 * A node id read back from the URL; anything malformed reads as the root (a stale link never
 * breaks the page).
 * @returns {{ id: string, type: 'racine'|'magasin'|'emplacement'|'non_range', magasinId: string|null, emplacementId: string|null }}
 */
export function lireNoeud(valeur) {
  const v = typeof valeur === "string" ? valeur.trim() : "";
  if (!v || v === NOEUD_RACINE) return RACINE;
  const [prefixe, a, b, ...reste] = v.split(":");
  if (reste.length || !a) return RACINE;
  if (prefixe === PREFIXES_NOEUD.MAGASIN && !b) return { id: v, type: "magasin", magasinId: a, emplacementId: null };
  if (prefixe === PREFIXES_NOEUD.NON_RANGE && !b) return { id: v, type: "non_range", magasinId: a, emplacementId: null };
  if (prefixe === PREFIXES_NOEUD.EMPLACEMENT && b) return { id: v, type: "emplacement", magasinId: a, emplacementId: b };
  return RACINE;
}

/** The parent node of a node (the root has none). */
export function parentDuNoeud(noeud) {
  const n = typeof noeud === "string" ? lireNoeud(noeud) : noeud;
  if (!n || n.type === "racine") return null;
  if (n.type === "magasin") return NOEUD_RACINE;
  return noeudMagasin(n.magasinId);
}

/** The tab of a magasin node (an unknown value reads as « Emplacements »). */
export function ongletValide(onglet) {
  return Object.values(ONGLETS_MAGASIN).includes(onglet) ? onglet : ONGLETS_MAGASIN.EMPLACEMENTS;
}

/** The level-2 node name of a node's lot page (`emplacement_id` of `GET /magasins/hierarchie`). */
export function noeudLots(noeud) {
  if (noeud.type === "emplacement") return noeud.emplacementId;
  if (noeud.type === "non_range") return HIERARCHIE.noeud_non_range;
  if (noeud.type === "magasin") return HIERARCHIE.noeud_tous;
  return null;
}

/** Which figure the « here » column of a lot page is (`quantite` of the level-2 rows). */
export function typeLots(noeud) {
  if (noeud.type === "emplacement") return "emplacement";
  if (noeud.type === "non_range") return "non_range";
  return "tous";
}

// ─── Sort of the detail grids (server, whitelisted per level) ───────────────────────────────────

/** The grid's sort (`{ by, order }`) kept only when the level's whitelist has it, else the default. */
export function triDuNiveau(niveau, tri) {
  const permis = niveau === 1 ? TRI_HIERARCHIE.emplacements : TRI_HIERARCHIE.lots;
  const defaut = niveau === 1 ? TRI_HIERARCHIE.emplacements_defaut : TRI_HIERARCHIE.lots_defaut;
  if (tri?.by && permis.includes(tri.by)) return { by: tri.by, order: tri.order === "desc" ? "desc" : "asc" };
  return { ...defaut };
}

const avecTri = (out, tri, defaut) => {
  if (tri && (tri.by !== defaut.by || tri.order !== defaut.order)) {
    out[SORT_PARAMS.BY] = tri.by;
    out[SORT_PARAMS.ORDER] = tri.order;
  }
  return out;
};

/**
 * The request of a magasin's level 1 (its emplacements + « Non rangé »).
 * @param {{ magasinId: string, produitId?: string|null, tri?: { by: string, order: string }|null }} p
 */
export function requeteNiveau1({ magasinId, produitId = null, tri = null }) {
  const out = { magasin_id: magasinId };
  if (produitId) out.produit_id = produitId;
  return avecTri(out, tri ? triDuNiveau(1, tri) : null, TRI_HIERARCHIE.emplacements_defaut);
}

/**
 * The request of a node's lot page (level 2): the node, the page, the server sort, the free text.
 * @returns {object|null} null for the root (no lot page)
 */
export function requeteLots(noeud, { page = 1, tri = null, recherche = "", produitId = null, limite = HIERARCHIE.lots_par_page } = {}) {
  const emplacement_id = noeudLots(noeud);
  if (!emplacement_id) return null;
  const out = { magasin_id: noeud.magasinId, emplacement_id, page, limit: limite };
  if (produitId) out.produit_id = produitId;
  const q = String(recherche ?? "").trim();
  if (q) out.q = q.slice(0, HIERARCHIE.recherche_max);
  return avecTri(out, tri ? triDuNiveau(2, tri) : null, TRI_HIERARCHIE.lots_defaut);
}

// ─── The tree ───────────────────────────────────────────────────────────────────────────────────

const parCode = (a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0);

/**
 * The visible items of the tree, in order: the root, the magasins (server order: PRINCIPAL first,
 * then code), and under each open magasin its emplacements (code order) then « Non rangé ». A
 * magasin's children come from its level-1 answer (`niveaux1`, lazily loaded); while it loads or
 * fails, the magasin item carries `etat` and the component draws the busy / retry row.
 *
 * @param {{ niveau0: object|null, niveaux1: Map<string, { data?: object, loading?: boolean, error?: unknown }>,
 *           ouverts: Set<string>, racineOuverte?: boolean }} p
 */
export function elementsArbre({ niveau0, niveaux1, ouverts, racineOuverte = true }) {
  const out = [];
  const magasins = niveau0?.magasins ?? [];
  out.push({
    id: NOEUD_RACINE,
    type: "racine",
    niveau: 1,
    parentId: null,
    magasinId: null,
    libelle: niveau0?.racine?.libelle ?? null,
    unites: niveau0?.racine?.unites ?? null,
    aEnfants: magasins.length > 0,
    ouvert: racineOuverte,
  });
  if (!racineOuverte) return out;
  for (const m of magasins) {
    const id = noeudMagasin(m.id);
    const etat1 = niveaux1?.get?.(m.id) ?? null;
    const ouvert = Boolean(ouverts?.has?.(m.id));
    out.push({
      id,
      type: "magasin",
      niveau: 2,
      parentId: NOEUD_RACINE,
      magasinId: m.id,
      code: m.code,
      libelle: m.nom,
      unites: m.unites,
      lots: m.lots,
      principal: Boolean(m.is_principal),
      ferme: m.statut === STATUTS_MAGASIN.FERME,
      masque: m.visible_caissier === false,
      aEnfants: true,
      ouvert,
      etat: ouvert ? (etat1?.data ? null : etat1?.error ? "erreur" : "chargement") : null,
    });
    if (!ouvert || !etat1?.data) continue;
    const n1 = etat1.data;
    for (const e of [...(n1.emplacements ?? [])].sort(parCode)) {
      out.push({
        id: noeudEmplacement(m.id, e.id),
        type: "emplacement",
        niveau: 3,
        parentId: id,
        magasinId: m.id,
        emplacementId: e.id,
        libelle: e.code,
        zone: e.zone ?? null,
        unites: e.unites,
        lots: e.lots,
        aEnfants: false,
        ouvert: false,
      });
    }
    out.push({
      id: noeudNonRange(m.id),
      type: "non_range",
      niveau: 3,
      parentId: id,
      magasinId: m.id,
      libelle: n1.non_range?.libelle ?? null,
      unites: n1.non_range?.unites ?? 0,
      lots: n1.non_range?.lots ?? 0,
      aEnfants: false,
      ouvert: false,
      tronque: n1.tronque ? Math.max(0, (n1.total_emplacements ?? 0) - (n1.emplacements?.length ?? 0)) : 0,
    });
  }
  return out;
}

/**
 * Where a tree key moves the focus (WP-16 tree-grid grammar: ↑ ↓ move, → expand / first child,
 * ← collapse / parent, Début / Fin). Returns `{ index }` to move, `{ ouvrir: id }` / `{ fermer: id }`
 * to toggle, or null.
 */
export function actionArbre(items, index, touche) {
  const it = items[index];
  if (!it) return null;
  switch (touche) {
    case "ArrowDown":
      return index < items.length - 1 ? { index: index + 1 } : null;
    case "ArrowUp":
      return index > 0 ? { index: index - 1 } : null;
    case "Home":
      return { index: 0 };
    case "End":
      return { index: items.length - 1 };
    case "ArrowRight":
      if (!it.aEnfants) return null;
      if (!it.ouvert) return { ouvrir: it.id };
      return items[index + 1]?.parentId === it.id ? { index: index + 1 } : null;
    case "ArrowLeft": {
      if (it.aEnfants && it.ouvert) return { fermer: it.id };
      const p = items.findIndex((x) => x.id === it.parentId);
      return p >= 0 ? { index: p } : null;
    }
    default:
      return null;
  }
}

// ─── Figures ────────────────────────────────────────────────────────────────────────────────────

/** The root's tiles (value only when the payload carries it — the server redacts, never the UI). */
export function tuilesRacine(racine) {
  if (!racine) return null;
  return {
    unites: racine.unites ?? 0,
    lots: racine.lots ?? 0,
    produits: racine.produits ?? 0,
    valeur: racine.valeur_ht ?? null,
    ruptures: racine.ruptures ?? 0,
  };
}

/** A magasin's figures: its level-0 row (units, lots, products, value, breaches) + level 1 (non rangé). */
export function figuresMagasin(ligne0, niveau1) {
  if (!ligne0) return null;
  return {
    unites: ligne0.unites ?? 0,
    lots: ligne0.lots ?? 0,
    produits: ligne0.produits ?? 0,
    valeur: ligne0.valeur_ht ?? null,
    ruptures: ligne0.ruptures ?? 0,
    nonRange: niveau1?.non_range?.unites ?? null,
    emplacements: niveau1?.total_emplacements ?? niveau1?.emplacements?.length ?? null,
  };
}

/**
 * The rows of a magasin's « Emplacements » grid: the server-sorted emplacements, then the
 * « Non rangé » row pinned last (it is a node of the tree, never sorted among shelves).
 */
export function lignesEmplacements(niveau1, magasinId) {
  if (!niveau1) return [];
  const lignes = (niveau1.emplacements ?? []).map((e) => ({ ...e, noeud: noeudEmplacement(magasinId, e.id), nonRange: false }));
  lignes.push({
    id: noeudNonRange(magasinId),
    noeud: noeudNonRange(magasinId),
    code: niveau1.non_range?.libelle ?? null,
    unites: niveau1.non_range?.unites ?? 0,
    lots: niveau1.non_range?.lots ?? 0,
    nonRange: true,
  });
  return lignes;
}

// ─── Targeted refresh on topic `stock` (XA-17) ──────────────────────────────────────────────────

/**
 * What a `stock` event's keys touch on the workspace. The root (level 0, one grouped query) re-reads
 * on any placement or quantity key; a magasin's level 1 on its own key, one of its emplacements, or
 * a quantity movement when it is the one shown (the others are marked stale and re-read when they
 * are opened again); the visible lot page when it lists a named lot, its magasin or emplacement
 * moved, or quantities moved. No key list (or a `resync`) = everything visible.
 *
 * @param {string[]|undefined} cles
 * @param {{ noeud: object, magasinsCharges: Iterable<string>, emplacementsParMagasin: Map<string, string>,
 *           lotIds: Set<string> }} vue  `emplacementsParMagasin`: emplacement id → its magasin id
 * @returns {{ racine: boolean, recharger: Set<string>, perimes: Set<string>, detail: boolean }}
 */
export function effetInvalidation(cles, { noeud, magasinsCharges = [], emplacementsParMagasin = new Map(), lotIds = new Set() }) {
  const charges = new Set(magasinsCharges);
  const visible = noeud?.magasinId ?? null;
  const tout = { racine: true, recharger: new Set(charges), perimes: new Set(), detail: true };
  if (!Array.isArray(cles) || cles.length === 0) return tout;
  const out = { racine: false, recharger: new Set(), perimes: new Set(), detail: false };
  const toucheMagasin = (m) => {
    if (!m) return;
    out.racine = true;
    if (charges.has(m)) out.recharger.add(m);
    if (m === visible) out.detail = true;
  };
  for (const c of cles) {
    if (c === CLES_STOCK_UI.magasins) out.racine = true;
    else if (c === CLES_STOCK_UI.lots) {
      // a quantity moved somewhere: the shown magasin re-reads, the others go stale
      out.racine = true;
      out.detail = out.detail || Boolean(visible);
      for (const m of charges) (m === visible ? out.recharger : out.perimes).add(m);
    } else if (c.startsWith(CLES_STOCK_UI.prefixeMagasin)) toucheMagasin(c.slice(CLES_STOCK_UI.prefixeMagasin.length));
    else if (c.startsWith(CLES_STOCK_UI.prefixeEmplacement)) {
      const e = c.slice(CLES_STOCK_UI.prefixeEmplacement.length);
      const m = emplacementsParMagasin.get(e);
      if (m) toucheMagasin(m);
      else out.racine = true; // a new or moved shelf this page has not seen yet
      if (noeud?.type === "emplacement" && noeud.emplacementId === e) out.detail = true;
    } else if (c.startsWith(CLES_STOCK_UI.prefixeLot) && lotIds.has(c.slice(CLES_STOCK_UI.prefixeLot.length))) out.detail = true;
  }
  for (const m of out.recharger) out.perimes.delete(m);
  return out;
}

/**
 * Whether a `stock` event concerns the list of the « Emplacements » or « Transferts » view: the
 * shelves' figures move with any placement or quantity key (a sale can trim a shelf, XMD-01-07);
 * the transfer documents only with a placement move (`magasins:vue-ensemble`, published by every
 * transfer validation — a sale never names it). No key list (or a `resync`) = re-read.
 */
export function vueConcernee(vue, cles) {
  if (!Array.isArray(cles) || cles.length === 0) return true;
  if (vue === MAGASINS_VUES.TRANSFERTS) return cles.includes(CLES_STOCK_UI.magasins);
  if (vue === MAGASINS_VUES.EMPLACEMENTS) {
    return cles.some(
      (c) =>
        c === CLES_STOCK_UI.magasins ||
        c === CLES_STOCK_UI.lots ||
        c.startsWith(CLES_STOCK_UI.prefixeEmplacement) ||
        c.startsWith(CLES_STOCK_UI.prefixeMagasin),
    );
  }
  return true;
}

// ─── « Répartir » (PUT /stock/lots/:id/placements, XD-96) ───────────────────────────────────────

let cleSuivante = 0;
const nouvelleCle = () => `e${++cleSuivante}`;

/** Read a typed quantity: a non-negative integer, `null` when empty, `NaN` when invalid. */
export function lireQuantite(v) {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return Number.NaN;
  return Number(s);
}

/**
 * The editable state of the dialog from the placement read: one line per live magasin (0 included),
 * its shelves, the closed magasins (they may keep or lower, never receive). `emplacementAjout`
 * pre-adds an empty shelf line under its magasin (« Ranger un lot ici »).
 *
 * @param {object} placement  `GET /stock/lots/:id/placements`
 * @param {{ emplacementAjout?: { emplacement_id: string, code: string, magasin_id: string }|null }} [opts]
 */
export function etatRepartition(placement, { emplacementAjout = null } = {}) {
  const lignes = (placement?.magasins ?? []).map((m) => ({
    magasin_id: m.magasin_id,
    code: m.code,
    nom: m.nom,
    principal: Boolean(m.is_principal),
    ferme: m.statut === STATUTS_MAGASIN.FERME,
    initial: m.quantite,
    quantite: String(m.quantite),
    emplacements: (m.emplacements ?? []).map((e) => ({ cle: nouvelleCle(), emplacement_id: e.emplacement_id, code: e.code, quantite: String(e.quantite) })),
  }));
  if (emplacementAjout) {
    const l = lignes.find((x) => x.magasin_id === emplacementAjout.magasin_id);
    if (l && !l.emplacements.some((e) => e.emplacement_id === emplacementAjout.emplacement_id)) {
      l.emplacements.push({ cle: nouvelleCle(), emplacement_id: emplacementAjout.emplacement_id, code: emplacementAjout.code, quantite: "" });
    }
  }
  return { total: Number(placement?.total ?? 0), lignes };
}

/** An empty shelf line for a magasin line (the user picks the emplacement). */
export const ligneEmplacementVide = () => ({ cle: nouvelleCle(), emplacement_id: null, code: null, quantite: "" });

/** Σ placed and what is left to place (negative = too many). */
export function resteRepartition(etat) {
  const place = etat.lignes.reduce((s, l) => s + (Number.isFinite(lireQuantite(l.quantite)) ? lireQuantite(l.quantite) ?? 0 : 0), 0);
  return { place, reste: etat.total - place };
}

/**
 * The refusals said before sending — the server's own rules and sentences (XD-96): a quantity per
 * magasin (integer ≥ 0), no unit INTO a closed magasin, each shelf chosen once and Σ shelves ≤ its
 * magasin, Σ magasins = the lot.
 * @returns {{ global: string|null, lignes: Record<string, string>, emplacements: Record<string, string> }}
 */
export function erreursRepartition(etat) {
  const out = { global: null, lignes: {}, emplacements: {} };
  const vus = new Map();
  for (const l of etat.lignes) {
    const q = lireQuantite(l.quantite);
    if (q === null || Number.isNaN(q) || q > REPARTITION_CHAMPS.quantite_max) {
      out.lignes[l.magasin_id] = REPARTIR.quantiteInvalide;
      continue;
    }
    if (l.ferme && q > l.initial) {
      out.lignes[l.magasin_id] = REPARTIR.magasinFerme(l.nom);
      continue;
    }
    let somme = 0;
    for (const e of l.emplacements) {
      const qe = lireQuantite(e.quantite);
      if (!e.emplacement_id) out.emplacements[e.cle] = REPARTIR.emplacementRequis;
      else if (vus.has(e.emplacement_id)) out.emplacements[e.cle] = REPARTIR.emplacementEnDouble(e.code);
      else if (qe === null || Number.isNaN(qe) || qe > REPARTITION_CHAMPS.quantite_max) out.emplacements[e.cle] = REPARTIR.quantiteInvalide;
      if (e.emplacement_id) vus.set(e.emplacement_id, e.cle);
      if (Number.isFinite(qe)) somme += qe;
    }
    if (somme > q) out.lignes[l.magasin_id] = REPARTIR.emplacementsDepassent(l.nom, q);
  }
  if (!Object.keys(out.lignes).length && resteRepartition(etat).reste !== 0) out.global = REPARTIR.somme(etat.total);
  return out;
}

export const repartitionValide = (erreurs) =>
  !erreurs.global && !Object.keys(erreurs.lignes).length && !Object.keys(erreurs.emplacements).length;

/**
 * The PUT body: every magasin line with its shelves as displayed (a line's `emplacements` replaces
 * that magasin's shelves — XD-96); a shelf at 0 is dropped (it holds nothing).
 */
export function corpsRepartition(etat) {
  return etat.lignes.map((l) => ({
    magasin_id: l.magasin_id,
    quantite: lireQuantite(l.quantite) ?? 0,
    emplacements: l.emplacements
      .filter((e) => e.emplacement_id && (lireQuantite(e.quantite) ?? 0) > 0)
      .map((e) => ({ emplacement_id: e.emplacement_id, quantite: lireQuantite(e.quantite) })),
  }));
}

/** True when the edited state differs from the placement read (nothing to send otherwise). */
export function repartitionModifiee(etat, placement) {
  const avant = new Map((placement?.magasins ?? []).map((m) => [m.magasin_id, m]));
  return corpsRepartition(etat).some((l) => {
    const a = avant.get(l.magasin_id);
    if (!a || a.quantite !== l.quantite) return true;
    const ea = new Map((a.emplacements ?? []).map((e) => [e.emplacement_id, e.quantite]));
    if (ea.size !== l.emplacements.length) return true;
    return l.emplacements.some((e) => ea.get(e.emplacement_id) !== e.quantite);
  });
}

// ─── « Ajuster la répartition » (POST /magasins/:id/inventaire/ajuster) ─────────────────────────

/**
 * The count of one lot in one magasin: what the system holds there, the most it may be counted at
 * (the server's `capacite`), and whether this magasin is the lot's reservoir (refused: it absorbs
 * the difference of the others).
 */
export function etatAjustement(placement, magasinId) {
  const ligne = (placement?.magasins ?? []).find((m) => m.magasin_id === magasinId) ?? null;
  const reservoir = (placement?.magasins ?? []).find((m) => m.magasin_id === placement?.reservoir_id) ?? null;
  return {
    ligne,
    actuel: ligne?.quantite ?? 0,
    capacite: ligne?.capacite ?? 0,
    estReservoir: Boolean(ligne) && placement?.reservoir_id === magasinId,
    reservoirNom: reservoir?.nom ?? null,
  };
}

/** @returns {Record<string, string>} field → message (empty = valid) */
export function erreursAjustement({ compte, motif }, etat) {
  const out = {};
  const q = lireQuantite(compte);
  if (q === null || Number.isNaN(q) || q > etat.capacite) out.compte = AJUSTER.compteInvalide(etat.capacite);
  else if (q === etat.actuel) out.compte = AJUSTER.aucunEcart;
  if (!String(motif ?? "").trim()) out.motif = AJUSTER.motifRequis;
  else if (String(motif).trim().length > REPARTITION_CHAMPS.motif_max) out.motif = FORM_MAGASIN.trop(REPARTITION_CHAMPS.motif_max);
  return out;
}

export const corpsAjustement = (stockLotId, { compte, motif }) => ({
  stock_lot_id: stockLotId,
  quantite_apres: lireQuantite(compte),
  motif: String(motif).trim(),
});

// ─── « Transférer » (POST /transferts, then /validate) ──────────────────────────────────────────

/** A lot picked from the source magasin becomes a line (the same lot twice stays one line). */
export function ajouterLigneTransfert(lignes, lot) {
  if (!lot?.stock_lot_id || lignes.some((l) => l.stock_lot_id === lot.stock_lot_id)) return lignes;
  return [
    ...lignes,
    {
      stock_lot_id: lot.stock_lot_id,
      produit_nom: lot.produit_nom ?? null,
      numero_lot: lot.numero_lot ?? null,
      date_peremption: lot.date_peremption ?? null,
      detenu: Number(lot.quantite ?? 0),
      quantite: String(lot.quantiteProposee ?? lot.quantite ?? ""),
    },
  ];
}

/** Lines and units of a transfer draft (valid quantities only). */
export function resumeTransfert(lignes) {
  const unites = lignes.reduce((s, l) => s + (Number.isFinite(lireQuantite(l.quantite)) ? lireQuantite(l.quantite) ?? 0 : 0), 0);
  return { lignes: lignes.length, unites };
}

/** @returns {{ source?: string, destination?: string, lignes?: string, quantites: Record<string, string>, motif?: string }} */
export function erreursTransfert({ source, destination, lignes, motif }) {
  const out = { quantites: {} };
  if (!source) out.source = TRANSFERER.sourceRequise;
  if (!destination) out.destination = TRANSFERER.destinationRequise;
  else if (source && source === destination) out.destination = TRANSFERER.memeMagasin;
  if (!lignes.length) out.lignes = TRANSFERER.aucuneLigne;
  else if (lignes.length > TRANSFERT_CHAMPS.lignes_max) out.lignes = TRANSFERER.quantiteMax(TRANSFERT_CHAMPS.lignes_max);
  for (const l of lignes) {
    const q = lireQuantite(l.quantite);
    const max = Math.min(l.detenu, TRANSFERT_CHAMPS.quantite_max);
    if (q === null || Number.isNaN(q) || q < 1 || q > max) out.quantites[l.stock_lot_id] = TRANSFERER.quantiteMax(max);
  }
  if (String(motif ?? "").trim().length > TRANSFERT_CHAMPS.motif_max) out.motif = FORM_MAGASIN.trop(TRANSFERT_CHAMPS.motif_max);
  return out;
}

export const transfertValide = (e) => !e.source && !e.destination && !e.lignes && !e.motif && !Object.keys(e.quantites).length;

export const corpsTransfert = ({ source, destination, lignes, motif }) => ({
  from_magasin_id: source,
  to_magasin_id: destination,
  motif: String(motif ?? "").trim() || null,
  lignes: lignes.map((l) => ({ source_lot_id: l.stock_lot_id, quantite: lireQuantite(l.quantite) })),
});

/** The magasins a transfer may send units to: live, open, not the source (XD-96). */
export const destinationsPossibles = (magasins, source) =>
  (magasins ?? []).filter((m) => m.id !== source && m.statut !== STATUTS_MAGASIN.FERME);

// ─── Magasin and emplacement forms ──────────────────────────────────────────────────────────────

const texte = (v) => String(v ?? "").trim();

export function formulaireMagasin(magasin) {
  const type = magasin?.type ?? TYPE_MAGASIN_DEFAUT;
  return {
    code: magasin?.code ?? "",
    nom: magasin?.nom ?? "",
    type,
    wilaya: magasin?.wilaya ?? "",
    adresse: magasin?.adresse ?? "",
    statut: magasin?.statut ?? STATUTS_MAGASIN.ACTIF,
    visible_caissier: type === TYPE_MAGASIN_COMPTOIR || magasin?.visible_caissier !== false,
    principal: Boolean(magasin?.is_principal),
  };
}

/**
 * X XWP-97 (XO-23 (7)) — « Visible aux caissiers en vente » is locked checked for a magasin of type
 * Comptoir (the server refuses any other value).
 */
export const visibiliteVerrouillee = (f) => f?.type === TYPE_MAGASIN_COMPTOIR;

/** The form after a type change: choosing Comptoir checks « Visible aux caissiers en vente ». */
export const avecType = (f, type) => ({ ...f, type, ...(type === TYPE_MAGASIN_COMPTOIR ? { visible_caissier: true } : {}) });

export function erreursMagasin(f) {
  const out = {};
  if (!texte(f.code)) out.code = FORM_MAGASIN.requis;
  else if (texte(f.code).length > MAGASIN_CHAMPS.code_max) out.code = FORM_MAGASIN.trop(MAGASIN_CHAMPS.code_max);
  if (!texte(f.nom)) out.nom = FORM_MAGASIN.requis;
  else if (texte(f.nom).length > MAGASIN_CHAMPS.nom_max) out.nom = FORM_MAGASIN.trop(MAGASIN_CHAMPS.nom_max);
  if (texte(f.wilaya).length > MAGASIN_CHAMPS.wilaya_max) out.wilaya = FORM_MAGASIN.trop(MAGASIN_CHAMPS.wilaya_max);
  if (texte(f.adresse).length > MAGASIN_CHAMPS.adresse_max) out.adresse = FORM_MAGASIN.trop(MAGASIN_CHAMPS.adresse_max);
  return out;
}

/**
 * The create / update body. The PRINCIPAL keeps its type and stays open (the server refuses
 * otherwise, MAGASIN_PRINCIPAL_PROTEGE): those fields are never sent for it; an update sends the
 * changed fields only.
 */
export function corpsMagasin(f, initial = null) {
  const complet = {
    code: texte(f.code),
    nom: texte(f.nom),
    wilaya: texte(f.wilaya) || null,
    adresse: texte(f.adresse) || null,
    visible_caissier: visibiliteVerrouillee(f) || Boolean(f.visible_caissier),
    ...(f.principal ? {} : { type: f.type === TYPE_MAGASIN_PRINCIPAL ? TYPE_MAGASIN_DEFAUT : f.type, statut: f.statut }),
  };
  if (!initial) return complet;
  const avant = corpsMagasin(formulaireMagasin(initial));
  return Object.fromEntries(Object.entries(complet).filter(([k, v]) => avant[k] !== v));
}

/** The code as stored (upper case, trimmed — the service's normalisation). */
export const normaliserCode = (code) => texte(code).toUpperCase();

export function formulaireEmplacement(emplacement, { magasinId = null } = {}) {
  return {
    code: emplacement?.code ?? "",
    magasin_id: emplacement?.magasin?.id ?? emplacement?.magasin_id ?? magasinId ?? "",
    zone: emplacement?.zone ?? "",
    produit: emplacement?.produit ? { id: emplacement.produit.id, nom: emplacement.produit.nom_commercial } : null,
    notes: emplacement?.notes ?? "",
  };
}

export function erreursEmplacement(f) {
  const out = {};
  const code = normaliserCode(f.code);
  if (!code) out.code = FORM_EMPLACEMENT.requis;
  else if (code.length < EMPLACEMENT_CHAMPS.longueur_min || code.length > EMPLACEMENT_CHAMPS.longueur_max || !EMPLACEMENT_CHAMPS.motif.test(code)) {
    out.code = FORM_EMPLACEMENT.codeFormat;
  }
  if (!f.magasin_id) out.magasin_id = FORM_EMPLACEMENT.requis;
  if (texte(f.zone).length > EMPLACEMENT_CHAMPS.zone_max) out.zone = FORM_EMPLACEMENT.trop(EMPLACEMENT_CHAMPS.zone_max);
  if (texte(f.notes).length > EMPLACEMENT_CHAMPS.notes_max) out.notes = FORM_EMPLACEMENT.trop(EMPLACEMENT_CHAMPS.notes_max);
  return out;
}

/**
 * The create / update body (an update sends the changed fields only). `magasinParDefaut` is the
 * magasin a legacy emplacement without one reads as (PRINCIPAL, XD-05): the initial form is read
 * with it too, so an unchanged field is never sent as a move (the server refuses moving a shelf
 * that holds units — EMPLACEMENT_OCCUPE).
 */
export function corpsEmplacement(f, initial = null, { magasinParDefaut = null } = {}) {
  const complet = {
    code: normaliserCode(f.code),
    magasin_id: f.magasin_id || null,
    zone: texte(f.zone) ? texte(f.zone).toUpperCase() : null,
    produit_id: f.produit?.id ?? null,
    notes: texte(f.notes) || null,
  };
  if (!initial) return complet;
  const avant = corpsEmplacement(formulaireEmplacement(initial, { magasinId: magasinParDefaut }));
  return Object.fromEntries(Object.entries(complet).filter(([k, v]) => avant[k] !== v));
}

// ─── Lists of the other two views ───────────────────────────────────────────────────────────────

/** `GET /emplacements` params of the « Emplacements » view. */
export function parametresEmplacements({ magasinId = null, recherche = "", page = 1, tri = null }) {
  const out = { page, limit: TRI_EMPLACEMENTS.limite };
  if (magasinId) out.magasin_id = magasinId;
  const q = texte(recherche);
  if (q) out.q = q;
  const t = tri?.by && TRI_EMPLACEMENTS.cles.includes(tri.by) ? tri : null;
  return avecTri(out, t, TRI_EMPLACEMENTS.defaut);
}

/** `GET /transferts` params of the « Transferts » view. */
export function parametresTransferts({ statut = null, magasinId = null, recherche = "", page = 1, tri = null }) {
  const out = { page, limit: LISTE_TRANSFERTS.limite };
  if (statut) out.statut = statut;
  if (magasinId) out.magasin_id = magasinId;
  const q = texte(recherche).slice(0, LISTE_TRANSFERTS.recherche_max);
  if (q) out.q = q;
  const t = tri?.by && LISTE_TRANSFERTS.tri.includes(tri.by) ? tri : null;
  return avecTri(out, t, LISTE_TRANSFERTS.defaut);
}

// ─── Links between the screens (§20 « explicit, navigable ») ────────────────────────────────────

/** Stock & lots filtered on a magasin (the « Magasin » facet of `/stock`). */
export const lienStockMagasin = (magasinId) => `/stock?${MAGASINS_PARAMS.MAGASIN}=${encodeURIComponent(magasinId)}`;

/** A node of the workspace (back from Stock & lots, « Voir dans Magasins » of the product panel). */
export function lienNoeud(noeud, { produitId = null } = {}) {
  const p = new URLSearchParams();
  if (noeud && noeud !== NOEUD_RACINE) p.set(MAGASINS_PARAMS.NOEUD, noeud);
  if (produitId) p.set(MAGASINS_PARAMS.PRODUIT_FILTRE, produitId);
  const s = p.toString();
  return `${MAGASINS_ROUTE}${s ? `?${s}` : ""}`;
}

/**
 * `/emplacements` (the legacy screen and its menu leaf, XMD-01-23) → the « Emplacements » view of
 * the workspace, every other query parameter kept (`?q=AA` stays a search).
 */
export function redirectionEmplacements(search = "") {
  const p = new URLSearchParams(search);
  p.set(MAGASINS_PARAMS.VUE, MAGASINS_VUES.EMPLACEMENTS);
  return `${MAGASINS_ROUTE}?${p.toString()}`;
}

// ─── The page state ─────────────────────────────────────────────────────────────────────────────

/** The view of `?vue=` (anything else reads as « Vue d'ensemble »). */
export function vueValide(vue) {
  return Object.values(MAGASINS_VUES).includes(vue) ? vue : MAGASINS_VUES.ENSEMBLE;
}

/** The page number of `?page=` (1 for anything that is not a positive integer). */
export function pageValide(page) {
  const n = Number.parseInt(String(page ?? ""), 10);
  return Number.isFinite(n) && n > 1 ? n : 1;
}

/**
 * The server sort the visible grid accepts (URL `sort_by` / `sort_order`) and its default: level 1
 * on a magasin's « Emplacements » tab, level 2 on a lot page, the two lists of the other views.
 * The root's magasins grid sorts in the browser (one row per magasin): `null`.
 */
export function triDuContexte(vue, noeud, onglet) {
  if (vue === MAGASINS_VUES.EMPLACEMENTS) return { cles: TRI_EMPLACEMENTS.cles, defaut: TRI_EMPLACEMENTS.defaut };
  if (vue === MAGASINS_VUES.TRANSFERTS) return { cles: LISTE_TRANSFERTS.tri, defaut: LISTE_TRANSFERTS.defaut };
  if (!noeud || noeud.type === "racine") return null;
  if (noeud.type === "magasin" && ongletValide(onglet) === ONGLETS_MAGASIN.EMPLACEMENTS) {
    return { cles: TRI_HIERARCHIE.emplacements, defaut: TRI_HIERARCHIE.emplacements_defaut };
  }
  if (noeud.type === "magasin" && ongletValide(onglet) === ONGLETS_MAGASIN.SEUILS) return null;
  return { cles: TRI_HIERARCHIE.lots, defaut: TRI_HIERARCHIE.lots_defaut };
}

/**
 * What a user may do on the workspace — the server's permissions (`magasin`, `emplacement`,
 * `transfert`, `stock`), mirrored for display only: the server refuses each write anyway.
 * @param {{ role?: string } | null | undefined} user
 */
export function droitsMagasins(user) {
  return Object.freeze({
    gerer: can(user, "magasin", "manage"),
    placer: can(user, "magasin", "allocate"),
    emplacementCreer: can(user, "emplacement", "create"),
    emplacementModifier: can(user, "emplacement", "update"),
    emplacementSupprimer: can(user, "emplacement", "delete"),
    voirTransferts: can(user, "transfert", "read"),
    transferer: can(user, "transfert", "create"),
    validerTransfert: can(user, "transfert", "manage"),
    corriger: can(user, "stock", "correct"),
    // the blocked / reserved layers and the values (a cost-blind role reads « vendable » only)
    couches: can(user, "stock", "bloquer"),
  });
}

/** The magasin a lot row of a node belongs to (the node's), for Transférer / Ajuster / Corriger. */
export const magasinDuNoeud = (noeud) => noeud?.magasinId ?? null;

/** The id of the PRINCIPAL magasin of a level-0 answer (where a legacy emplacement without one is). */
export const principalDe = (niveau0) => (niveau0?.magasins ?? []).find((m) => m.is_principal)?.id ?? null;

/** emplacement id → magasin id, from every loaded level 1 (the refresh rule reads it). */
export function emplacementsParMagasin(niveaux1) {
  const out = new Map();
  for (const [m, etat] of niveaux1 ?? []) for (const e of etat?.data?.emplacements ?? []) out.set(e.id, m);
  return out;
}

// ─── Refusals of the API ────────────────────────────────────────────────────────────────────────

/** The server's answer of a refused write: code, sentence, details, first field. */
export function lireRefus(err, defaut) {
  const corps = err?.response?.data ?? err?.data ?? err ?? {};
  const champ = Array.isArray(corps.errors) && corps.errors[0]?.field ? String(corps.errors[0].field).replace(/^(body|query|params)\./, "") : null;
  return {
    code: corps.code ?? null,
    message: corps.errors?.[0]?.message ?? corps.message ?? err?.message ?? defaut,
    details: corps.details ?? null,
    champ,
    statut: err?.response?.status ?? err?.status ?? null,
  };
}
